import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { memo, useCallback, useEffect, useRef } from 'react';
import Config from '../../../../config';
import IconService from '../../../Services/IconService';
import { IVehicleData, IVehicles, IVehiclesData } from '../../Vehicles/types';
import { BluecoinsData } from '../Bluecoins/types';
import { getBluecoinIcon } from '../Bluecoins/BluecoinService';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import { isOnlinePanelOpenAtom } from '../../OnlinePanel/OnlinePanelState';
import { bluecoinsAtom, isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import { isRidePlayerOpenAtom, isStreetviewEnabledAtom } from '../OnlineMapState';
import { vehiclesDataAtom } from '../../../store/recoil/vehiclesData';
import { isNavigatorDisabledAtom } from '../../Navigator/NavigatorState';
import useAnalytics from '../../../hooks/useAnalytics';

interface StreetViewLayerProps {
  googleMap: google.maps.Map | undefined;
}

function StreetViewLayer(props: StreetViewLayerProps) {
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const allVehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const streetViewMarkers = React.useRef<{ [vehicleId: number]: google.maps.Marker | null }>({});
  const vehiclesData = useRecoilValue<IVehiclesData>(vehiclesDataAtom);
  const setIsOnlinePanelOpen = useSetRecoilState<number | false>(isOnlinePanelOpenAtom);
  const setIsRidePlayerOpen = useSetRecoilState<number | boolean>(isRidePlayerOpenAtom);
  const [isStreetviewEnabled, setIsStreetviewEnabled] =
    useRecoilState<boolean>(isStreetviewEnabledAtom);
  const [isNavigatorDisabled, setIsNavigatorDisabled] = useRecoilState<boolean>(isNavigatorDisabledAtom);
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const bluecoinsData = useRecoilValue<BluecoinsData>(bluecoinsAtom);
  const streetViewBleMarkers = useRef<{ [bluecoinId: number]: google.maps.Marker | null }>({});
  const { trackEvent } = useAnalytics();
  const hasTrackedEvent = useRef(false);

  const createMarker = useCallback(
    (id: number, vehicle: IVehicleData) => {
      if (
        props.googleMap &&
        vehicle.position &&
        vehicle.position.latitude &&
        vehicle.position.longitude
      ) {
        const iconUrl = IconService.getVehicleIcon(
          id === Config.mucha ? 999 : allVehicles[id]?.vehicleTypeId,
          vehicle.status && vehicle.drivingMode === 'business'
            ? vehicle.status
            : vehicle.drivingMode === 'private'
              ? 'PRIVATE'
              : 'UNKNOWN',
          vehicle.position.angle,
          true,
          1
          // isOnlinePanelOpen === id
        );

        const icon = {
          url: iconUrl,
          scaledSize: new google.maps.Size(80, 80),
          anchor: new google.maps.Point(25, 25),
        };

        return new google.maps.Marker({
          icon,
          position: { lat: vehicle.position?.latitude, lng: vehicle.position?.longitude },
          map: props.googleMap,
        });
      }
      return null;
    },
    [allVehicles, props.googleMap]
  );

  // Create marker for Bluecoin
  const createBluecoinMarker = useCallback(
    (bluecoinId: number, isActive: boolean) => {
      if (
        props.googleMap &&
        isBluecoinsOpen &&
        typeof bluecoinsData[bluecoinId].position.latitude === 'number' &&
        typeof bluecoinsData[bluecoinId].position.longitude === 'number'
      ) {
        const iconUrl = getBluecoinIcon(isActive ? 'ACTIVE' : 'INACTIVE');

        const icon = {
          url: iconUrl,
          scaledSize: new google.maps.Size(50, 50),
          anchor: new google.maps.Point(25, 25),
        };

        return new google.maps.Marker({
          icon,
          position: {
            lat: Number(bluecoinsData[bluecoinId].position.latitude),
            lng: Number(bluecoinsData[bluecoinId].position.longitude),
          },
          map: props.googleMap,
        });
      }
      return null;
    },
    [props.googleMap, isBluecoinsOpen, bluecoinsData]
  );

  // Enable navigator when it was disabled due to streetview
  useEffect(() => {
    if (isNavigatorDisabled && !isStreetviewEnabled) {
      setIsNavigatorDisabled(false);
    }
  }, [isNavigatorDisabled, isStreetviewEnabled, setIsNavigatorDisabled]);

  const handleStreeview = useCallback(
    (streetView: google.maps.StreetViewPanorama) => {
      if (streetViewMarkers && streetViewMarkers.current) {
        Object.keys(streetViewMarkers.current).forEach((vehicleId: string) => {
          const marker = streetViewMarkers.current[Number(vehicleId)];
          if (marker) {
            marker.setVisible(false);
            marker.setIcon(null);
            marker.setMap(null);
          }
          delete streetViewMarkers.current[Number(vehicleId)];
        });
      }

      if (streetView.getVisible() && vehiclesData) {
        if (streetView.getVisible() && !hasTrackedEvent.current) {
          trackEvent(['map', 'click', 'Show street view for vehicles']);
          hasTrackedEvent.current = true;
        }
        setIsStreetviewEnabled(true);
        setIsOnlinePanelOpen(false);
        setIsNavigatorDisabled(true);
        setIsRidePlayerOpen(false);

        selectedVehicles.forEach((vehicleId: number) => {
          const vehicle = vehiclesData[vehicleId];
          if (
            vehicle &&
            streetViewMarkers &&
            streetViewMarkers.current &&
            !streetViewMarkers.current[Number(vehicleId)]
          ) {
            const marker = createMarker(vehicleId, vehicle);
            if (marker) {
              streetViewMarkers.current[vehicleId] = marker;
            }
          }
        });
      } else {
        setIsStreetviewEnabled(false);
        setIsNavigatorDisabled(false);

        if (streetViewMarkers && streetViewMarkers.current) {
          streetViewMarkers.current = {};
        }
      }

      if (streetViewBleMarkers && streetViewBleMarkers.current) {
        Object.keys(streetViewBleMarkers.current).forEach((bluecoinId: string) => {
          const marker = streetViewBleMarkers.current[Number(bluecoinId)];
          if (marker) {
            marker.setVisible(false);
            marker.setIcon(null);
            marker.setMap(null);
          }
          delete streetViewBleMarkers.current[Number(bluecoinId)];
        });
      }
      // Logic Bluecoins
      if (streetView.getVisible() && isBluecoinsOpen && bluecoinsData) {
        if (streetView.getVisible() && !hasTrackedEvent.current) {
          trackEvent(['map', 'click', 'Show street view for bluecoins']);
          hasTrackedEvent.current = true;
        }
        Object.keys(bluecoinsData).forEach((bluecoinId) => {
          const isActive =
            bluecoinsData[Number(bluecoinId)] && bluecoinsData[Number(bluecoinId)].attached;
          const marker = createBluecoinMarker(Number(bluecoinId), isActive);
          if (marker) {
            streetViewBleMarkers.current[Number(bluecoinId)] = marker;
          }
        });
      } else {
        Object.keys(streetViewBleMarkers.current).forEach((bluecoinId) => {
          const marker = streetViewBleMarkers.current[Number(bluecoinId)];
          if (marker) {
            marker.setMap(null);
          }
        });
        streetViewBleMarkers.current = {};
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createMarker,
      selectedVehicles,
      setIsNavigatorDisabled,
      setIsOnlinePanelOpen,
      setIsRidePlayerOpen,
      setIsStreetviewEnabled,
      vehiclesData,
      createBluecoinMarker,
      isBluecoinsOpen,
      bluecoinsData,
    ]
  );

  useEffect(() => {
    if (props.googleMap && props.googleMap.getStreetView) {
      const streetView: google.maps.StreetViewPanorama = props.googleMap.getStreetView();
      google.maps.event.addListener(streetView, 'visible_changed', () => {
        handleStreeview(streetView);
      });
    }
    return () => {
      hasTrackedEvent.current = false;
    };
  }, [
    createMarker,
    handleStreeview,
    props.googleMap,
    selectedVehicles,
    setIsNavigatorDisabled,
    setIsStreetviewEnabled,
    vehiclesData,
  ]);

  return null;
}

export default memo(StreetViewLayer);
